import { useEffect, useState } from "react";
import { manifest, type Manifest } from "./chain";
import { BRAND, Lockup } from "./Brand";
import Overview from "./pages/Overview";
import Faucet from "./pages/Faucet";
import Explorer from "./pages/Explorer";
import Wallet from "./pages/Wallet";
import SettlX from "./pages/SettlX";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "settlx", label: "SettlX" },
  { id: "wallet", label: "Wallet" },
  { id: "explorer", label: "Explorer" },
  { id: "faucet", label: "Faucet" },
] as const;

type TabId = (typeof TABS)[number]["id"];

function currentTab(): TabId {
  const h = window.location.hash.replace(/^#\/?/, "");
  return (TABS.find((t) => t.id === h)?.id ?? "overview") as TabId;
}

export default function App() {
  const [tab, setTab] = useState<TabId>(currentTab);
  const [m, setM] = useState<Manifest>();
  const [err, setErr] = useState<string>();

  useEffect(() => {
    const onHash = () => setTab(currentTab());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    manifest().then(setM, (e: Error) => setErr(e.message));
  }, []);

  const go = (id: TabId) => { window.location.hash = `/${id}`; setTab(id); };

  return (
    <>
      <header className="top">
        <div className="top-in">
          <Lockup note="devnet · SettlX" />
          <nav>
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => go(t.id)}
                aria-current={tab === t.id ? "page" : undefined}
              >
                {t.label}
              </button>
            ))}
            {/*
              The app, not a tab. These five pages are operator tools over the
              raw chain -- addresses, hex, a faucet -- and SettlX is the
              product a counterparty actually uses. Making it a sixth tab
              would imply it is another view of the same thing; it is a
              different audience, so it gets a different affordance.
            */}
            <a className="app-link" href="/app/">
              Open SettlX <span aria-hidden="true">&rarr;</span>
            </a>
          </nav>
        </div>
      </header>

      <div className="shell">
        {err && (
          <div className="card" style={{ marginTop: 28 }}>
            <h1>No devnet manifest</h1>
            <p className="sub">{err}</p>
            <div className="note warn">
              The apps read contract addresses from <code>/devnet.json</code>, which
              <code> scripts/deploy-devnet.ts</code> writes. Nothing here can work until a
              deployment has happened, and guessing addresses would be worse than saying so.
            </div>
          </div>
        )}
        {!err && !m && <p className="sub" style={{ marginTop: 28 }}><span className="spin" /> loading devnet…</p>}
        {m && tab === "overview" && <Overview m={m} />}
        {m && tab === "settlx" && <SettlX m={m} />}
        {m && tab === "wallet" && <Wallet m={m} />}
        {m && tab === "explorer" && <Explorer m={m} />}
        {m && tab === "faucet" && <Faucet m={m} />}

        <footer className="foot">
          <span>{BRAND.strapline}</span>
          <span>secure.qwilon.com</span>
        </footer>
      </div>
    </>
  );
}
